import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { ZoomIn, ZoomOut, Locate, Grid as GridIcon, MousePointer2, Check, RotateCcw, ExternalLink } from "lucide-react";

import { devWarn } from "@/lib/log";

const GRID_UNITS = 100;
const BLOCK_UNIT = 10;
const GRID_PX = GRID_UNITS * BLOCK_UNIT; // 1000
const CENTER_LO = 40, CENTER_HI = 60;

// Persist a per-browser visitor UUID so backend hover-dedup can count unique
// hovers per (block, visitor, date). Stored in localStorage so refresh-safe.
const VISITOR_KEY = "pixelempire_visitor_id";
function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = (crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

/**
 * Animated hover-card. Positions itself near the cursor but clamps within the
 * viewport so it never gets cut off at the edges. Shows the advertiser's logo,
 * brand name, short description and a prominent "Visit site" button.
 *
 * When `pinned=true` (touch/mobile), a small close button appears and the
 * card stays visible until dismissed or the Visit button is pressed.
 */
function HoverCard({ block, pointer, pinned = false, onDismiss }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ left: pointer.x + 18, top: pointer.y + 18, ready: false });

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const CARD_W = el.offsetWidth || 280;
    const CARD_H = el.offsetHeight || 160;
    const GAP = 16;
    let left = pointer.x + GAP;
    let top = pointer.y + GAP;
    if (left + CARD_W > window.innerWidth - 8) left = pointer.x - CARD_W - GAP;
    if (top + CARD_H > window.innerHeight - 8) top = pointer.y - CARD_H - GAP;
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    setPos({ left, top, ready: true });
  }, [pointer.x, pointer.y, block?.id]);

  const openSite = (e) => {
    e.stopPropagation();
    if (!block?.destination_url) return;
    http.post(`/pixels/${block.id}/click`).catch(() => {});
    window.open(block.destination_url, "_blank", "noopener,noreferrer");
    if (pinned && onDismiss) onDismiss();
  };

  const desc = block.description || block.hover_slogan || "";

  return (
    <div
      ref={cardRef}
      className={`pixel-hover-card ${pos.ready ? "pixel-hover-card--in" : "pixel-hover-card--hidden"}`}
      style={{ left: pos.left, top: pos.top }}
      data-testid="pixel-hover-card"
      role="dialog"
      aria-label={`Advertiser ${block.brand_name}`}
      onClick={(e) => e.stopPropagation()}
    >
      {pinned && (
        <button
          type="button"
          className="pixel-hover-card__close"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
          aria-label="Dismiss preview"
          data-testid="hover-card-close"
        >
          ×
        </button>
      )}
      <div className="flex items-start gap-3">
        {block.image_url ? (
          <img
            src={block.image_url}
            alt={block.brand_name || ""}
            className="w-14 h-14 rounded-lg object-contain bg-white/5 border border-white/10 flex-shrink-0"
            data-testid="hover-card-logo"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div
            className="font-display text-lg leading-tight text-white truncate"
            data-testid="hover-card-brand"
          >
            {block.brand_name || "Advertiser"}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-cyan-300 font-mono">
            <MousePointer2 className="w-3 h-3" />
            <span data-testid="hover-card-clicks">{(block.click_count || 0).toLocaleString()}</span>
            <span className="text-[var(--fg-muted)]">clicks</span>
            <span className="text-[var(--fg-muted)]">·</span>
            <span>{block.width * BLOCK_UNIT}×{block.height * BLOCK_UNIT}px</span>
          </div>
        </div>
      </div>
      {desc && (
        <p
          className="mt-3 text-sm text-[var(--fg-muted)] leading-snug line-clamp-3"
          data-testid="hover-card-desc"
        >
          {desc}
        </p>
      )}
      <button
        type="button"
        onClick={openSite}
        disabled={!block.destination_url}
        className="pixel-hover-card__cta"
        data-testid="hover-card-visit"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Visit site
      </button>
    </div>
  );
}

// Module-level TransformWrapper props — stable references (avoids re-mounting on every render).
const WHEEL_CFG = { step: 0.15 };
const DOUBLECLICK_CFG = { mode: "toggle" };
const CONTENT_STYLE = { width: GRID_PX, height: GRID_PX };
const WRAPPER_STYLE = {
  width: "100%",
  height: "min(72vh, 780px)",
  borderRadius: 14,
  overflow: "hidden",
  background: "#050507",
};

/**
 * PixelEmpire AI grid. 100×100 units × 10px each.
 *
 * Rendering strategy: the canvas paints the grid lines, base color, center-zone
 * glow and gradient placeholders for image-less blocks. Image blocks are
 * rendered as absolutely-positioned <img> elements ON TOP of the canvas so the
 * browser natively rescales them during CSS transforms (zoom) — this keeps
 * logos razor-sharp at any zoom level instead of a stretched canvas bitmap.
 *
 * Exposes an imperative panToBlock(block) via forwarded ref.
 */
const PixelGrid = forwardRef(function PixelGrid({ onClusterSelect, refreshKey = 0, highlightBlockId = null, categoryFilter = "" }, ref) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const transformApiRef = useRef(null);
  const [blocks, setBlocks] = useState([]);
  const [hoverBlock, setHoverBlock] = useState(null);
  // Pinned block for touch/mobile devices — first tap pins the hover-card so
  // the visitor can read the brand + description, second interaction (Visit
  // button OR tap-outside) navigates or dismisses. Desktop keeps its
  // instant-navigate-on-click behaviour.
  const [pinnedBlock, setPinnedBlock] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  // Which block has been logged for hover-tracking this session (Set of ids)
  const hoveredLoggedRef = useRef(new Set());
  const [flashId, setFlashId] = useState(null);
  // ── Explicit pointer state machine ─────────────────────────────────────────
  //   isDragging  →  true between pointerDown (on an empty cell) and pointerUp
  //   dragAnchor  →  {startX, startY, curX, curY, pointerId} while dragging
  // The onClusterSelect callback (which opens the checkout panel) is fired
  // STRICTLY on onPointerUp — never on onPointerDown or onPointerMove.
  const [isDragging, setIsDragging] = useState(false);
  const [dragAnchor, setDragAnchor] = useState(null);
  // Locked selection awaiting user confirmation via the floating toolbar.
  // While set, the neon-purple highlight stays anchored on the grid and the
  // "Done selecting your real estate space?" toolbar hovers above/below it.
  // Only 'Yes, Proceed to Checkout' fires onClusterSelect (opens the panel).
  const [pendingSelection, setPendingSelection] = useState(null);
  const [toolbarPos, setToolbarPos] = useState(null);
  const pendingHighlightRef = useRef(null);

  useEffect(() => {
    let alive = true;
    http.get("/pixels").then(({ data }) => {
      if (!alive) return;
      setBlocks(data.blocks || []);
    }).catch((err) => {
      devWarn("[grid] failed to load blocks:", err?.message);
    });
    return () => { alive = false; };
  }, [refreshKey]);

  // Draw: base, grid lines, center-zone, gradient placeholders ONLY.
  // Images are DOM <img> overlays below.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (canvas.width !== GRID_PX * dpr) {
      canvas.width = GRID_PX * dpr;
      canvas.height = GRID_PX * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, GRID_PX, GRID_PX);

    // Base
    ctx.fillStyle = "#0a0a10";
    ctx.fillRect(0, 0, GRID_PX, GRID_PX);

    // Center-zone glow
    ctx.fillStyle = "rgba(139, 92, 246, 0.06)";
    ctx.fillRect(CENTER_LO * BLOCK_UNIT, CENTER_LO * BLOCK_UNIT,
                 (CENTER_HI - CENTER_LO) * BLOCK_UNIT, (CENTER_HI - CENTER_LO) * BLOCK_UNIT);

    // Placeholder gradient for image-less blocks (image blocks are DOM <img> below)
    blocks.forEach((b) => {
      if (b.image_url) return;
      const px = b.x * BLOCK_UNIT;
      const py = b.y * BLOCK_UNIT;
      const pw = b.width * BLOCK_UNIT;
      const ph = b.height * BLOCK_UNIT;
      // Category filter — if a filter is active and this block doesn't match,
      // paint it heavily dimmed so it visually disappears without leaving a
      // gap in the grid geometry (still clickable for accessibility).
      const dimmed = categoryFilter && (b.category || "").toLowerCase() !== categoryFilter.toLowerCase();
      ctx.globalAlpha = dimmed ? 0.08 : 1;
      const g = ctx.createLinearGradient(px, py, px + pw, py + ph);
      g.addColorStop(0, b.tier === "premium" ? "#f59e0b" : "#8b5cf6");
      g.addColorStop(1, "#06b6d4");
      ctx.fillStyle = g;
      ctx.fillRect(px, py, pw, ph);
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "bold 8px monospace";
      const label = (b.brand_name || "").slice(0, Math.max(1, Math.floor(pw / 6)));
      ctx.fillText(label, px + 2, py + 10);
      ctx.globalAlpha = 1;
    });

    // Subtle unit grid
    ctx.strokeStyle = "rgba(255,255,255,0.03)";
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= GRID_UNITS; i++) {
      ctx.beginPath();
      ctx.moveTo(i * BLOCK_UNIT + 0.5, 0);
      ctx.lineTo(i * BLOCK_UNIT + 0.5, GRID_PX);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * BLOCK_UNIT + 0.5);
      ctx.lineTo(GRID_PX, i * BLOCK_UNIT + 0.5);
      ctx.stroke();
    }

    // Center-zone border
    ctx.strokeStyle = "rgba(139, 92, 246, 0.5)";
    ctx.setLineDash([8, 6]);
    ctx.lineWidth = 1.5;
    ctx.strokeRect(
      CENTER_LO * BLOCK_UNIT, CENTER_LO * BLOCK_UNIT,
      (CENTER_HI - CENTER_LO) * BLOCK_UNIT, (CENTER_HI - CENTER_LO) * BLOCK_UNIT,
    );
    ctx.setLineDash([]);
  }, [blocks, categoryFilter]);

  const eventToUnit = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const ux = Math.floor(((e.clientX - rect.left) / rect.width) * GRID_UNITS);
    const uy = Math.floor(((e.clientY - rect.top) / rect.height) * GRID_UNITS);
    return {
      ux: Math.max(0, Math.min(GRID_UNITS - 1, ux)),
      uy: Math.max(0, Math.min(GRID_UNITS - 1, uy)),
    };
  };

  const hitBlock = (ux, uy) =>
    blocks.find((b) => ux >= b.x && ux < b.x + b.width && uy >= b.y && uy < b.y + b.height);

  // Compute the current drag bounding rectangle in units (min/max form).
  const dragRect = useMemo(() => {
    if (!dragAnchor) return null;
    const x = Math.min(dragAnchor.startX, dragAnchor.curX);
    const y = Math.min(dragAnchor.startY, dragAnchor.curY);
    const width = Math.abs(dragAnchor.curX - dragAnchor.startX) + 1;
    const height = Math.abs(dragAnchor.curY - dragAnchor.startY) + 1;
    return { x, y, width, height };
  }, [dragAnchor]);

  // Does the rectangle overlap any claimed / reserved block?
  const overlapsClaimed = (rect) => {
    if (!rect) return false;
    return blocks.some(
      (b) =>
        rect.x < b.x + b.width &&
        rect.x + rect.width > b.x &&
        rect.y < b.y + b.height &&
        rect.y + rect.height > b.y,
    );
  };

  // ── onPointerDown ──────────────────────────────────────────────────────────
  // Registers the starting coordinates and flips isDragging=true.
  // MUST NOT open the checkout panel or fire any onClusterSelect.
  //
  // Also: if a pending selection is showing its "Done?" toolbar, ANY new
  // pointerdown on the grid is treated as "user changed their mind" — we
  // discard the pending rectangle so the fresh drag starts clean.
  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const { ux, uy } = eventToUnit(e);
    const hit = hitBlock(ux, uy);
    if (hit) return; // clicks on claimed blocks are handled by onClick handler
    if (pendingSelection) setPendingSelection(null);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
    setDragAnchor({ startX: ux, startY: uy, curX: ux, curY: uy, pointerId: e.pointerId });
    setIsDragging(true);
  };

  // ── onPointerMove ──────────────────────────────────────────────────────────
  // Only updates the drawing state (dragAnchor). No pop-ups. No panel opens.
  const onPointerMove = (e) => {
    const { ux, uy } = eventToUnit(e);
    setTooltipPos({ x: e.clientX, y: e.clientY });
    if (isDragging) {
      setDragAnchor((d) => (d ? { ...d, curX: ux, curY: uy } : d));
      setHoverBlock(null);
    } else {
      const hit = hitBlock(ux, uy);
      setHoverBlock(hit || null);
      // Log a unique hover ONCE per (visitor, block, session). Backend also
      // deduplicates per UTC date. Fire-and-forget; failures are silent.
      if (hit && !hoveredLoggedRef.current.has(hit.id)) {
        hoveredLoggedRef.current.add(hit.id);
        http
          .post(`/pixels/${hit.id}/hover`, null, {
            headers: { "X-Visitor-Id": getVisitorId() },
          })
          .catch((err) => devWarn("[grid] hover tracking failed:", err?.message));
      }
    }
  };

  // ── onPointerUp ────────────────────────────────────────────────────────────
  // Snaps the highlighted rectangle, validates it doesn't overlap a claimed
  // block, and LOCKS it as a pending selection. Does NOT open the checkout
  // sidebar — that only happens when the user clicks "Yes, Proceed to
  // Checkout" in the floating toolbar rendered next to the pending rectangle.
  const onPointerUp = (e) => {
    if (!isDragging || !dragAnchor) return;
    try {
      e.currentTarget?.releasePointerCapture?.(dragAnchor.pointerId);
    } catch { /* pointer already released */ }
    const cur = dragAnchor;
    setDragAnchor(null);
    setIsDragging(false);

    const rect = {
      x: Math.min(cur.startX, cur.curX),
      y: Math.min(cur.startY, cur.curY),
      width: Math.abs(cur.curX - cur.startX) + 1,
      height: Math.abs(cur.curY - cur.startY) + 1,
    };

    if (overlapsClaimed(rect)) {
      toast.warning("Your selection overlaps a claimed block. Pick an empty area.", { duration: 3500 });
      return;
    }
    setPendingSelection(rect); // toolbar renders — user must confirm
  };

  const confirmSelection = () => {
    if (!pendingSelection) return;
    const sel = pendingSelection;
    setPendingSelection(null);
    onClusterSelect?.(sel);
  };

  const cancelSelection = () => {
    setPendingSelection(null);
    setToolbarPos(null);
  };

  // Anchor the toolbar to the pending rectangle's on-screen bounding box.
  // Recompute on scroll + resize so the toolbar tracks even as the page moves.
  useLayoutEffect(() => {
    if (!pendingSelection) { setToolbarPos(null); return; }
    const compute = () => {
      const el = pendingHighlightRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const TOOLBAR_W = 320, TOOLBAR_H = 88, GAP = 12;
      const spaceBelow = window.innerHeight - r.bottom;
      const placeBelow = spaceBelow >= TOOLBAR_H + GAP;
      const top = placeBelow ? r.bottom + GAP : Math.max(12, r.top - TOOLBAR_H - GAP);
      const centerX = r.left + r.width / 2 - TOOLBAR_W / 2;
      const left = Math.min(Math.max(12, centerX), window.innerWidth - TOOLBAR_W - 12);
      setToolbarPos({ left, top });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [pendingSelection]);

  // Global outside-click while a pending selection is showing → auto-cancel.
  // Any click that isn't on the pending highlight itself nor on the toolbar
  // is treated as "user changed their mind" per spec #3.
  useEffect(() => {
    if (!pendingSelection) return;
    const handler = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest?.("[data-testid='confirm-toolbar']")) return;
      if (t.closest?.("[data-testid='pending-selection-highlight']")) return;
      // If click landed on the canvas wrap → a NEW drag is starting, the
      // onPointerDown handler already resets pending. Skip here.
      if (t.closest?.(".pixel-canvas-wrap")) return;
      cancelSelection();
    };
    // Register on next tick so the click that TRIGGERED the pending state
    // (pointerUp → synthetic click) doesn't immediately cancel it.
    const tid = setTimeout(() => document.addEventListener("mousedown", handler), 50);
    return () => {
      clearTimeout(tid);
      document.removeEventListener("mousedown", handler);
    };
  }, [pendingSelection]);

  // ── Reset trap ─────────────────────────────────────────────────────────────
  // Wired to the "Cancel & Reset Selection" button inside the checkout panel.
  // Wipes all drag state so the neon-violet highlight disappears from the grid
  // and the user can drag a fresh rectangle without residue.
  useEffect(() => {
    const handler = () => {
      setDragAnchor(null);
      setIsDragging(false);
      setPendingSelection(null);
    };
    window.addEventListener("pixel:selection-reset", handler);
    return () => window.removeEventListener("pixel:selection-reset", handler);
  }, []);

  const onClickBlock = (e) => {
    // Only handles clicks on ALREADY CLAIMED blocks.
    // Desktop: click opens the destination immediately.
    // Touch/mobile (`hover: none`): first tap pins the hover-card so visitors
    // can preview brand + description; the card's "Visit site" button then
    // navigates. Tapping the same block twice also navigates.
    const { ux, uy } = eventToUnit(e);
    const hit = hitBlock(ux, uy);
    if (!hit) {
      if (pinnedBlock) setPinnedBlock(null);
      return;
    }
    const isTouchDevice =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: none)").matches;
    const goToSite = () => {
      http.post(`/pixels/${hit.id}/click`).catch((err) => {
        devWarn("[grid] click tracking failed:", err?.message);
      });
      if (hit.destination_url) {
        window.open(hit.destination_url, "_blank", "noopener,noreferrer");
      }
    };
    if (isTouchDevice) {
      // Tap the same pinned block again → navigate. Otherwise pin card first.
      if (pinnedBlock && pinnedBlock.id === hit.id) {
        setPinnedBlock(null);
        goToSite();
        return;
      }
      setPinnedBlock(hit);
      setTooltipPos({ x: e.clientX, y: e.clientY });
      // Log the equivalent of a "hover" so mobile visitors count in analytics.
      if (!hoveredLoggedRef.current.has(hit.id)) {
        hoveredLoggedRef.current.add(hit.id);
        http
          .post(`/pixels/${hit.id}/hover`, null, {
            headers: { "X-Visitor-Id": getVisitorId() },
          })
          .catch(() => {});
      }
      return;
    }
    goToSite();
  };

  const soldPct = useMemo(() => {
    const s = blocks.reduce((s, b) => s + b.width * b.height, 0);
    return ((s / (GRID_UNITS * GRID_UNITS)) * 100).toFixed(2);
  }, [blocks]);

  const panningCfg = useMemo(
    () => ({ excluded: ["input", "button"], velocityDisabled: true, disabled: isDragging }),
    [isDragging],
  );
  const canvasWrapStyle = useMemo(
    () => ({ width: GRID_PX, height: GRID_PX, cursor: isDragging ? "crosshair" : "pointer", touchAction: "none" }),
    [isDragging],
  );

  const dragInvalid = dragRect && overlapsClaimed(dragRect);

  // Imperative pan-to-block for the header search bar.
  useImperativeHandle(ref, () => ({
    panToBlock(block) {
      if (!block || !transformApiRef.current) return;
      const api = transformApiRef.current;
      const targetX = (block.x + block.width / 2) * BLOCK_UNIT;
      const targetY = (block.y + block.height / 2) * BLOCK_UNIT;
      const wrap = document.querySelector(".pixel-grid-frame");
      const viewportW = wrap ? wrap.clientWidth : 800;
      const viewportH = Math.min(window.innerHeight * 0.72, 780);
      // Zoom so block occupies ~35% of the viewport min-dim.
      const targetSpan = Math.max(block.width, block.height) * BLOCK_UNIT;
      const desired = Math.min(viewportW, viewportH) * 0.35 / targetSpan;
      const scale = Math.max(1.5, Math.min(20, desired));
      const positionX = viewportW / 2 - targetX * scale;
      const positionY = viewportH / 2 - targetY * scale;
      api.setTransform(positionX, positionY, scale, 800, "easeOutCubic");
      setFlashId(block.id);
      setTimeout(() => setFlashId(null), 2600);
    },
  }));

  // Also listen for a window-level pan event (dispatched from the header search).
  useEffect(() => {
    const handler = (e) => {
      const block = e.detail?.block;
      if (block && ref && "current" in ref) {
        ref.current?.panToBlock(block);
      }
    };
    window.addEventListener("pixel:pan-to", handler);
    return () => window.removeEventListener("pixel:pan-to", handler);
  }, [ref]);

  // Auto-pan when highlightBlockId prop changes.
  useEffect(() => {
    if (!highlightBlockId || blocks.length === 0) return;
    const target = blocks.find((b) => b.id === highlightBlockId);
    if (target && ref && "current" in ref) {
      ref.current?.panToBlock(target);
    }
  }, [highlightBlockId, blocks, ref]);

  return (
    <div className="pixel-grid-frame p-3 sm:p-4" data-testid="pixel-grid">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 pl-1">
          <GridIcon className="w-4 h-4 text-neon-violet" />
          <span className="text-sm font-medium">100 × 100 units</span>
          <span className="text-xs font-mono text-[var(--fg-muted)]">{soldPct}% claimed</span>
        </div>
        <div className="grow" />
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
          Click a cell · or press & drag to size a custom rectangle
        </div>
      </div>

      <TransformWrapper
        minScale={0.6}
        maxScale={30}
        initialScale={0.95}
        centerOnInit
        wheel={WHEEL_CFG}
        doubleClick={DOUBLECLICK_CFG}
        panning={panningCfg}
        onInit={(api) => { transformApiRef.current = api; }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="absolute right-6 top-24 z-20 flex flex-col gap-1">
              <button data-testid="zoom-in" onClick={() => zoomIn()} className="p-2 rounded-full glass hover:scale-105 transition"><ZoomIn className="w-4 h-4" /></button>
              <button data-testid="zoom-out" onClick={() => zoomOut()} className="p-2 rounded-full glass hover:scale-105 transition"><ZoomOut className="w-4 h-4" /></button>
              <button data-testid="zoom-reset" onClick={() => resetTransform()} className="p-2 rounded-full glass hover:scale-105 transition"><Locate className="w-4 h-4" /></button>
            </div>
            <TransformComponent
              wrapperStyle={WRAPPER_STYLE}
              contentStyle={CONTENT_STYLE}
            >
              <div
                ref={overlayRef}
                className="pixel-canvas-wrap"
                style={canvasWrapStyle}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={(e) => { if (isDragging) onPointerUp(e); else setHoverBlock(null); }}
                onClick={onClickBlock}
              >
                <canvas
                  ref={canvasRef}
                  style={{ imageRendering: "auto", width: GRID_PX, height: GRID_PX, display: "block" }}
                />
                {/* Unified image rendering — for every purchased block we
                    compute a single bounding rectangle wrapper from its
                    stored (x, y, width, height). The <img> inside stretches
                    to fill that rectangle with object-fit: cover, so a wide
                    or tall multi-unit block shows ONE crisp image across the
                    whole cluster — never tiled or repeated per unit. */}
                {blocks.filter((b) => b.image_url).map((b) => {
                  const dimmed =
                    categoryFilter &&
                    (b.category || "").toLowerCase() !== categoryFilter.toLowerCase();
                  return (
                    <div
                      key={b.id}
                      className={`pixel-block-container ${flashId === b.id ? "pixel-flash" : ""}`}
                      style={{
                        left: b.x * BLOCK_UNIT,
                        top: b.y * BLOCK_UNIT,
                        width: b.width * BLOCK_UNIT,
                        height: b.height * BLOCK_UNIT,
                        opacity: dimmed ? 0.1 : 1,
                        filter: dimmed ? "grayscale(0.7)" : "none",
                        transition: "opacity 200ms ease, filter 200ms ease",
                      }}
                      data-testid={`block-container-${b.id}`}
                    >
                      <img
                        src={b.image_url}
                        alt={b.brand_name || ""}
                        className="pixel-img-unified"
                        draggable={false}
                        data-testid={`block-img-${b.id}`}
                      />
                    </div>
                  );
                })}
                {/* Live drag highlight — vibrant neon-violet rectangle that
                    tracks the pointer in real-time. Semi-transparent so the
                    grid + placeholders show through. Flips to amber when the
                    selection overlaps a claimed block. */}
                {dragRect && (
                  <div
                    data-testid="drag-highlight"
                    className={`drag-highlight-box ${dragInvalid ? "drag-highlight-invalid" : ""}`}
                    style={{
                      left: dragRect.x * BLOCK_UNIT,
                      top: dragRect.y * BLOCK_UNIT,
                      width: dragRect.width * BLOCK_UNIT,
                      height: dragRect.height * BLOCK_UNIT,
                    }}
                  />
                )}
                {/* Locked pending rectangle — stays anchored on the canvas
                    while the floating toolbar asks the user to confirm. */}
                {pendingSelection && (
                  <div
                    ref={pendingHighlightRef}
                    data-testid="pending-selection-highlight"
                    className="drag-highlight-box pending-selection-box"
                    style={{
                      left: pendingSelection.x * BLOCK_UNIT,
                      top: pendingSelection.y * BLOCK_UNIT,
                      width: pendingSelection.width * BLOCK_UNIT,
                      height: pendingSelection.height * BLOCK_UNIT,
                    }}
                  />
                )}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>

      {(hoverBlock || pinnedBlock) && !isDragging && (
        <HoverCard
          block={hoverBlock || pinnedBlock}
          pointer={tooltipPos}
          pinned={!hoverBlock && !!pinnedBlock}
          onDismiss={() => setPinnedBlock(null)}
        />
      )}
      {dragRect && (
        <div
          data-testid="drag-size-overlay"
          className="drag-size-overlay"
          style={{ left: tooltipPos.x + 14, top: tooltipPos.y + 14 }}
        >
          <div className="text-xs uppercase tracking-widest opacity-70">Size</div>
          <div className="font-display text-xl leading-none">
            {dragRect.width} × {dragRect.height} <span className="text-xs opacity-70">blocks</span>
          </div>
          <div className="mt-1 font-mono text-[11px] opacity-80">
            {dragRect.width * BLOCK_UNIT}×{dragRect.height * BLOCK_UNIT}px · {dragRect.width * dragRect.height * 100} pixels
          </div>
          {!dragInvalid && (
            <div className="mt-1 font-mono text-[11px] text-cyan-300">
              ~${(dragRect.width * dragRect.height * 100 * 0.5).toFixed(2)}
            </div>
          )}
          {dragInvalid && (
            <div className="mt-1 text-[11px] text-amber-300">Overlaps a claimed block</div>
          )}
        </div>
      )}

      {/* Floating confirmation toolbar — the ONLY path from a locked
          selection to the checkout sidebar. Renders in fixed viewport
          coordinates so it stays crisp regardless of grid zoom/pan. */}
      {pendingSelection && toolbarPos && (
        <div
          data-testid="confirm-toolbar"
          className="confirm-toolbar"
          role="dialog"
          aria-label="Confirm your selection"
          style={{ left: toolbarPos.left, top: toolbarPos.top }}
        >
          <div className="confirm-toolbar-title">
            Done selecting your real estate space?
          </div>
          <div className="confirm-toolbar-sub">
            {pendingSelection.width}×{pendingSelection.height} blocks ·{" "}
            {pendingSelection.width * pendingSelection.height * 100} pixels ·{" "}
            <span className="text-cyan-300">${(pendingSelection.width * pendingSelection.height * 100 * 0.5).toFixed(2)}</span>
          </div>
          <div className="confirm-toolbar-actions">
            <button
              type="button"
              data-testid="confirm-yes-checkout"
              onClick={confirmSelection}
              className="btn-confirm-yes"
            >
              <Check className="w-4 h-4" /> Yes, Proceed to Checkout
            </button>
            <button
              type="button"
              data-testid="confirm-no-redraw"
              onClick={cancelSelection}
              className="btn-confirm-no"
            >
              <RotateCcw className="w-4 h-4" /> No, Redraw Shape
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default PixelGrid;
